import { useEffect, useState, useCallback, useMemo } from "react";
import Select from "react-select";
import { MaterialReactTable } from "material-react-table";
import api from "../Axios/api";
import Loader from "../common/Loader";
import { FileUp } from "lucide-react";
import * as XLSX from "xlsx";
const DEFAULT_FACTORY_ID = "666c0f5ebeca5ab2c65b2c85";

const factoryOptions = [
    {
        value: "666c0f5ebeca5ab2c65b2c85",
        label: "PolyBond Insulation",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c86",
        label: "PolyBond Project",
    },
    {
        value: "666c0f5ebeca5ab2c65b2c87",
        label: "PolyBond RockFibre",
    },
];

const allCategoryOption = {
    value: "",
    label: "All Categories",
};

const formatNumber = (value, digits = 3) =>
    Number(value ?? 0).toLocaleString("en-IN", {
        maximumFractionDigits: digits,
    });

const formatAmount = (value) =>
    Number(value ?? 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const StockSummaryReport = () => {
    // Report view toggle: "item" | "category"
    const [reportView, setReportView] = useState("item");

    const [selectedFactory, setSelectedFactory] = useState(
        factoryOptions.find(
            (factory) => factory.value === DEFAULT_FACTORY_ID
        )
    );

    const [selectedCategory, setSelectedCategory] =
        useState(allCategoryOption);

    const [categoryOptions, setCategoryOptions] = useState([
        allCategoryOption,
    ]);

    const getCurrentMonthRange = () => {
        const now = new Date();

        // Get current date and time in IST
        const istDate = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
        );

        const year = istDate.getFullYear();
        const month = istDate.getMonth();

        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");

            return `${y}-${m}-${d}`;
        };

        return {
            fromDate: formatDate(new Date(year, month, 1)),
            toDate: formatDate(new Date(year, month + 1, 0)),
        };
    };

    const initialDates = getCurrentMonthRange();

    const [fromDate, setFromDate] = useState(initialDates.fromDate);
    const [toDate, setToDate] = useState(initialDates.toDate);

    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loading, setLoading] = useState(false);

    const [stockData, setStockData] = useState([]);
    const [reportFooter, setReportFooter] = useState({
        openingAmt: 0,
        inAmt: 0,
        outAmt: 0,
        balAmt: 0,
    });

    // Fetch categories
    const fetchItemCategories = useCallback(async () => {
        setLoadingCategories(true);

        try {
            const response = await api.get(
                "/purchase-item-category/get-name"
            );

            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.data || [];

            const options = data.map((category) => ({
                value: category._id,
                label: category.itemcat_title,
            }));

            setCategoryOptions([
                allCategoryOption,
                ...options,
            ]);
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategoryOptions([allCategoryOption]);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    // Fetch stock summary report
    const fetchSummaryReport = useCallback(async () => {
        if (fromDate && toDate && fromDate > toDate) {
            setStockData([]);
            setReportFooter({
                openingAmt: 0,
                inAmt: 0,
                outAmt: 0,
                balAmt: 0,
            });
            return;
        }

        setLoading(true);

        try {
            const response = await api.get(
                "/inventory/stockSummaryReport",
                {
                    params: {
                        factory:
                            selectedFactory?.value ||
                            DEFAULT_FACTORY_ID,

                        category: selectedCategory?.value || "",

                        fromDate: fromDate || "",
                        toDate: toDate || "",

                        groupBy: reportView, // "item" or "category"
                    },
                }
            );  

            const result = response.data;

            setStockData(
                Array.isArray(result?.data) ? result.data : []
            );

            setReportFooter({
                openingAmt: Number(
                    result?.reportFooter?.openingAmt ?? 0
                ),
                inAmt: Number(
                    result?.reportFooter?.inAmt ?? 0
                ),
                outAmt: Number(
                    result?.reportFooter?.outAmt ?? 0
                ),
                balAmt: Number(
                    result?.reportFooter?.balAmt ?? 0
                ),
            });
        } catch (error) {
            console.error("Stock register API error:", error);

            setStockData([]);
            setReportFooter({
                openingAmt: 0,
                inAmt: 0,
                outAmt: 0,
                balAmt: 0,
            });
        } finally {
            setLoading(false);
        }
    }, [
        selectedFactory,
        selectedCategory,
        fromDate,
        toDate,
        reportView, // Refetch whenever toggle changes
    ]);

    // Load categories once
    useEffect(() => {
        fetchItemCategories();
    }, [fetchItemCategories]);

    // Reload report whenever a filter or view changes
    useEffect(() => {
        fetchSummaryReport();
    }, [fetchSummaryReport]);

    // Factory change
    const handleFactoryChange = (option) => {
        setSelectedFactory(option);
        setSelectedCategory(allCategoryOption);
    };
    const exportToExcel = (data, type) => {
    if (!data || data.length === 0) {
        alert("No data available to export");
        return;
    }

    let excelData = [];
    let columns = [];
    let sheetName = "";
    let fileName = "";

    if (type === "item") {
        // Item Wise Excel
        excelData = data.map((item, index) => ({
            "S.No.": index + 1,
            "Factory": item.factory || "-",
            "Category": item.category || "-",
            "Item Name": item.item || "-",
            "Unit": item.unit || "-",
            "Opening Qty": Number(item.openingQty || 0),
            "Opening Amount": Number(item.openingAmt || 0),
            "In Qty": Number(item.inQty || 0),
            "In Amount": Number(item.inAmt || 0),
            "Out Qty": Number(item.outQty || 0),
            "Out Amount": Number(item.outAmt || 0),
            "Closing Qty": Number(item.closingQty || 0),
            "Closing Amount": Number(item.closingAmt || 0),
        }));

        columns = [
            { wch: 8 },
            { wch: 25 },
            { wch: 20 },
            { wch: 25 },
            { wch: 12 },
            { wch: 15 },
            { wch: 18 },
            { wch: 12 },
            { wch: 18 },
            { wch: 12 },
            { wch: 18 },
            { wch: 15 },
            { wch: 18 },
        ];

        sheetName = "Item Wise";
        fileName = "Item_Wise_Stock_Register";

    } else if (type === "category") {
        // Category Wise Excel
        excelData = data.map((item, index) => ({
            "S.No.": index + 1,
            "Factory": item.factory || "-",
            "Category": item.category || "-",
            "Opening Amount": Number(item.openingAmt || 0),
            "In Amount": Number(item.inAmt || 0),
            "Out Amount": Number(item.outAmt || 0),
            "Closing Amount": Number(item.closingAmt || 0),
        }));

        columns = [
            { wch: 8 },
            { wch: 25 },
            { wch: 20 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
        ];

        sheetName = "Category Wise";
        fileName = "Category_Wise_Stock_Register";
    } else {
        alert("Invalid export type");
        return;
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    worksheet["!cols"] = columns;

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    XLSX.writeFile(
        workbook,
        `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
};

    // Dynamic Material React Table columns based on reportView
const columns = useMemo(() => {
    const baseColumns = [
        {
            id: "serialNumber",
            header: "S.No.",
            Cell: ({ row }) => row.index + 1,
            enableSorting: false,
            size: 70,
        },
        {
            accessorKey: "factory",
            header: "Factory",
            size: 180,
        },
        {
            accessorKey: "category",
            header: "Category",
            size: 160,
        },
    ];

    // Category Wise Columns
    if (reportView === "category") {
        return [
            ...baseColumns,
            {
                accessorKey: "openingAmt",
                header: "Opening Amount",
                Cell: ({ cell }) => formatAmount(cell.getValue()),
                size: 150,
            },
            {
                accessorKey: "inAmt",
                header: "In Amount",
                Cell: ({ cell }) => formatAmount(cell.getValue()),
                size: 140,
            },
            {
                accessorKey: "outAmt",
                header: "Out Amount",
                Cell: ({ cell }) => formatAmount(cell.getValue()),
                size: 140,
            },
            {
                accessorKey: "closingAmt",
                header: "Closing Amount",
                Cell: ({ cell }) => formatAmount(cell.getValue()),
                size: 150,
            },
        ];
    }

    // Item Wise Columns
    return [
        ...baseColumns,
        {
            accessorKey: "item",
            header: "Item Name",
            size: 220,
        },
        {
            accessorKey: "unit",
            header: "Unit",
            size: 90,
        },
        {
            accessorKey: "openingQty",
            header: "Opening Qty",
            Cell: ({ cell }) => formatNumber(cell.getValue()),
            size: 130,
        },
        {
            accessorKey: "openingAmt",
            header: "Opening Amount",
            Cell: ({ cell }) => formatAmount(cell.getValue()),
            size: 150,
        },
        {
            accessorKey: "inQty",
            header: "In Qty",
            Cell: ({ cell }) => formatNumber(cell.getValue()),
            size: 110,
        },
        {
            accessorKey: "inAmt",
            header: "In Amount",
            Cell: ({ cell }) => formatAmount(cell.getValue()),
            size: 140,
        },
        {
            accessorKey: "outQty",
            header: "Out Qty",
            Cell: ({ cell }) => formatNumber(cell.getValue()),
            size: 110,
        },
        {
            accessorKey: "outAmt",
            header: "Out Amount",
            Cell: ({ cell }) => formatAmount(cell.getValue()),
            size: 140,
        },
        {
            accessorKey: "closingQty",
            header: "Closing Qty",
            Cell: ({ cell }) => formatNumber(cell.getValue()),
            size: 130,
        },
        {
            accessorKey: "closingAmt",
            header: "Closing Amount",
            Cell: ({ cell }) => formatAmount(cell.getValue()),
            size: 150,
        },
    ];
}, [reportView]);

    return (
        <div className="min-h-screen bg-gray-50 p-5">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-bold text-gray-800">
                    Stock Summary Report
                </h1>

                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
                    <label
                        className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                            reportView === "item"
                                ? "bg-blue-50 text-blue-700"
                                : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                        <input
                            type="radio"
                            name="reportView"
                            value="item"
                            checked={reportView === "item"}
                            onChange={(e) => setReportView(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        Item Wise
                    </label>

                    <label
                        className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                            reportView === "category"
                                ? "bg-blue-50 text-blue-700"
                                : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                        <input
                            type="radio"
                            name="reportView"
                            value="category"
                            checked={reportView === "category"}
                            onChange={(e) => setReportView(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        Category Wise
                    </label>
                </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-4 rounded-xl bg-white p-4 shadow-sm md:grid-cols-2 xl:grid-cols-4">
                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Factory
                    </label>

                    <Select
                        options={factoryOptions}
                        value={selectedFactory}
                        onChange={handleFactoryChange}
                        placeholder="Select factory"
                        isClearable={false}
                    />
                </div>

                <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                        Select Category
                    </label>

                    <Select
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={(option) =>
                            setSelectedCategory(
                                option || allCategoryOption
                            )
                        }
                        placeholder="Select category"
                        isClearable={false}
                        isLoading={loadingCategories}
                        menuPortalTarget={
                            typeof document !== "undefined"
                                ? document.body
                                : undefined
                        }
                        menuPosition="fixed"
                        styles={{
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>

                <div>
                    <label
                        htmlFor="fromDate"
                        className="mb-2 block text-sm font-medium text-gray-700"
                    >
                        From Date
                    </label>

                    <input
                        id="fromDate"
                        name="fromDate"
                        type="date"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={(event) =>
                            setFromDate(event.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label
                        htmlFor="toDate"
                        className="mb-2 block text-sm font-medium text-gray-700"
                    >
                        To Date
                    </label>

                    <input
                        id="toDate"
                        name="toDate"
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        onChange={(event) =>
                            setToDate(event.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                </div>
            </div>

            {/* Summary Cards */}
            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl bg-blue-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-600">
                        Opening Amount
                    </p>
                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        ₹ {formatAmount(reportFooter.openingAmt)}
                    </h2>
                </div>

                <div className="rounded-xl bg-cyan-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-600">
                        In Amount
                    </p>
                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        ₹ {formatAmount(reportFooter.inAmt)}
                    </h2>
                </div>

                <div className="rounded-xl bg-green-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-600">
                        Out Amount
                    </p>
                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        ₹ {formatAmount(reportFooter.outAmt)}
                    </h2>
                </div>

                <div className="rounded-xl bg-purple-100 p-4 shadow-sm">
                    <p className="text-center text-sm text-gray-600">
                        Closing Amount
                    </p>
                    <h2 className="mt-2 text-center text-xl font-bold text-gray-800">
                        ₹ {formatAmount(reportFooter.balAmt)}
                    </h2>
                </div>
            </div>
                           <button
                    onClick={() => exportToExcel(stockData, reportView)}
                    className="inline-flex items-center mb-2 gap-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white px-2 py-2.5 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                    <FileUp size={18} strokeWidth={2} />
                    Export
                </button>
            {/* Stock Register Table */}
            <div className="rounded-xl bg-white p-4 shadow-sm">
                {loading ? (
                    <Loader />
                ) : (
                    <MaterialReactTable
                        columns={columns}
                        data={stockData}
                        enablePagination
                        enableSorting
                        enableGlobalFilter
                        enableColumnFilters={false}
                        enableDensityToggle={false}
                        enableFullScreenToggle
                        enableHiding
                        enableStickyHeader
                        initialState={{
                            pagination: {
                                pageIndex: 0,
                                pageSize: 10,
                            },
                            density: "comfortable",
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 25, 50, 100],
                            showFirstButton: true,
                            showLastButton: true,
                        }}
                        muiTableContainerProps={{
                            sx: {
                                maxHeight: "600px",
                            },
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: {
                                borderRadius: "8px",
                                overflow: "hidden",
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                backgroundColor: "#f9fafb",
                                color: "#4b5563",
                                fontSize: "12px",
                                fontWeight: 600,
                                borderBottom: "1px solid #e5e7eb",
                                whiteSpace: "nowrap",
                            },
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                fontSize: "13px",
                                color: "#374151",
                                py: 1.5,
                                whiteSpace: "nowrap",
                            },
                        }}
                        renderEmptyRowsFallback={() => (
                            <div className="w-full py-8 text-center text-sm text-gray-500">
                                No stock records found.
                            </div>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

export default StockSummaryReport;